import { Controller, useForm } from "react-hook-form";
import { useCallback } from "react";
import { zodResolver } from "@hookform/resolvers/zod";

import { AdminProfile } from "@/core/types";
import { Button2 } from "@/ui2/atoms/Button";
import { PhoneNumberTextField } from "@/features/form/PhoneNumberTextField";
import { showToast } from "@/providers/ToastProvider";
import { TextField2 } from "@/ui2/molecules/TextField";

import { basicInfoSchema, type BasicInfoFormValues } from "../schema";
import { useUpdateAdminProfile } from "../api/mutations/useUpdateAdminProfile";

type BasicInfoFormProps = {
  isReadOnly: boolean;
  onCancel: () => void;
  profileData: AdminProfile;
};

export function BasicInfoForm({ isReadOnly, onCancel, profileData }: BasicInfoFormProps) {
  const { control, formState, handleSubmit, reset } = useForm<BasicInfoFormValues>({
    defaultValues: {
      email: profileData.email,
      mobile: profileData.mobile,
      name: profileData.name,
      user_type: profileData.user_type,
    },
    mode: "onSubmit",
    resolver: zodResolver(basicInfoSchema),
  });

  const { isPending, mutate } = useUpdateAdminProfile({
    meta: { component: "BasicInfoForm" },
    onError: () => {
      showToast([{ heading: "Failed to update profile. Please try again.", variant: "error" }]);
    },
    onSuccess: () => {
      onCancel();
      showToast([{ heading: "Profile updated successfully.", variant: "success" }]);
    },
  });

  const onSubmit = useCallback(
    (values: BasicInfoFormValues) => {
      mutate({ preferences: profileData.preferences ?? {}, ...values });
    },
    [mutate, profileData.preferences],
  );

  const handleCancel = useCallback(() => {
    reset();
    onCancel();
  }, [reset, onCancel]);

  const isSubmitDisabled = isPending || formState.isSubmitting;

  return (
    <form
      className="tw:grid tw:grid-cols-2 tw:gap-x-8 tw:gap-y-6"
      onSubmit={handleSubmit(onSubmit)}
    >
      <Controller
        control={control}
        name="user_type"
        render={({ field }) => (
          <TextField2
            hasStrongLabel
            /** user_type is always read-only */
            isReadOnly
            label="Customer Success Type"
            mode="input"
            onChange={field.onChange}
            value={field.value}
          />
        )}
      />

      <Controller
        control={control}
        name="name"
        render={({ field, fieldState }) => (
          <TextField2
            errorMessage={fieldState.error?.message}
            hasStrongLabel
            isInvalid={!!fieldState.error}
            isReadOnly={isReadOnly}
            isRequired={!isReadOnly}
            label="Full Name"
            mode="input"
            onChange={field.onChange}
            value={field.value}
          />
        )}
      />

      <Controller
        control={control}
        name="email"
        render={({ field, fieldState }) => (
          <TextField2
            errorMessage={fieldState.error?.message}
            hasStrongLabel
            isInvalid={!!fieldState.error}
            isReadOnly={isReadOnly}
            isRequired={!isReadOnly}
            label="Email"
            mode="input"
            onChange={field.onChange}
            value={field.value}
          />
        )}
      />

      <Controller
        control={control}
        name="mobile"
        render={({ field, fieldState }) => (
          <PhoneNumberTextField
            description={isReadOnly ? undefined : "Enter 10 digits, e.g. (555) 123-4567"}
            errorMessage={fieldState.error?.message}
            hasStrongLabel
            isInvalid={!!fieldState.error}
            isReadOnly={isReadOnly}
            isRequired={!isReadOnly}
            label="Mobile"
            phoneNumberInputProps={{
              onChange: field.onChange,
            }}
            value={field.value}
          />
        )}
      />

      {!isReadOnly && (
        <div className="tw:flex tw:justify-end tw:gap-3">
          <Button2
            isDisabled={isSubmitDisabled}
            onPress={handleCancel}
            size="md"
            type="button"
            variant="secondary"
          >
            Cancel
          </Button2>
          <Button2
            isDisabled={isSubmitDisabled}
            isLoading={isPending}
            size="md"
            type="submit"
            variant="primary"
          >
            Save
          </Button2>
        </div>
      )}
    </form>
  );
}
